import { Menu, MenuItem } from "@mui/material";
import { Button } from "pepsico-ds";
import { useCallback, useState } from "react";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import StoreGroupModal from "./StoreGroupModal";
import { downloadStoresCsv } from "./storeUtils";
import StoresTable from "./StoresTable";

const StoresListView = ({
  stores,
  allStores,
  loading,
  filterData,
  handlers,
  onAddSingleStore,
  onImportFile,
  onEditStore,
  onDeleteStore,
  onDeleteMultiple,
  onSaveGroup,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const handleExportStores = () => {
    downloadStoresCsv(allStores || stores, `stores-${Date.now()}.csv`);
  };

  const handleAddStoreClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddSingleStore = () => {
    handleMenuClose();
    onAddSingleStore();
  };

  const handleImportFile = () => {
    handleMenuClose();
    onImportFile();
  };

  const handleSelectionChange = useCallback((ids) => {
    setSelectedStoreIds(ids);
  }, []);

  const handleBulkAddToGroup = () => {
    setShowGroupModal(true);
  };

  const handleSaveGroupFromBulk = (groupName, storeIds) => {
    onSaveGroup(groupName, storeIds);
    setSelectedStoreIds([]);
  };

  const handleBulkDelete = () => {
    setDeleteConfirmation({ type: "bulk", ids: selectedStoreIds });
  };

  const handleExportSelected = () => {
    const selectedStores = stores.filter((store) =>
      selectedStoreIds.includes(store.id)
    );
    downloadStoresCsv(selectedStores, `stores-selected-${Date.now()}.csv`);
  };

  const handleRequestDeleteStore = useCallback((id, name) => {
    setDeleteConfirmation({ type: "single", id, name });
  }, []);

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmation) return;
    if (deleteConfirmation.type === "bulk") {
      if (onDeleteMultiple) {
        onDeleteMultiple(deleteConfirmation.ids);
      }
      setSelectedStoreIds([]);
    } else if (onDeleteStore) {
      onDeleteStore(deleteConfirmation.id, deleteConfirmation.name);
    }
    setDeleteConfirmation(null);
  };

  const selectedCount = selectedStoreIds.length;

  const deleteConfirmationTitle =
    deleteConfirmation?.type === "bulk"
      ? `Delete ${deleteConfirmation.ids.length} selected store${
          deleteConfirmation.ids.length > 1 ? "s" : ""
        }?`
      : `Delete "${deleteConfirmation?.name}"?`;

  return (
    <div className="stores-list-view">
      <div className="stores-list-actions">
        <Button
          text="Export Stores"
          variant="secondary"
          size="medium"
          iconLeading="file_download"
          onClick={handleExportStores}
        />
        <Button
          text="Add Store"
          onClick={handleAddStoreClick}
          size="medium"
          iconTrailing="arrow_drop_down"
          className="add-store-dropdown-btn"
        />
        <Menu
          anchorEl={menuAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleAddSingleStore} sx={{ minWidth: "180px" }}>
            Add Single Store
          </MenuItem>
          <MenuItem onClick={handleImportFile} sx={{ minWidth: "180px" }}>
            Import File
          </MenuItem>
        </Menu>
      </div>

      {selectedCount > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-selection-count">
            {selectedCount} of {stores.length} rows selected
          </span>
          <div className="bulk-action-buttons">
            <Button
              text="Delete"
              variant="secondary"
              size="small"
              onClick={handleBulkDelete}
            />
            <Button
              text="Add to store group"
              variant="primary"
              size="small"
              onClick={handleBulkAddToGroup}
            />
            <Button
              text="Export selected"
              variant="secondary"
              size="small"
              onClick={handleExportSelected}
            />
          </div>
        </div>
      )}

      <StoresTable
        stores={stores}
        loading={loading}
        filterData={filterData}
        handleEdit={onEditStore}
        handleSoftDelete={handleRequestDeleteStore}
        handleFilterChange={handlers.handleFilterChange}
        selectable={true}
        selectedStoreIds={selectedStoreIds}
        onSelectionChange={handleSelectionChange}
      />

      <StoreGroupModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        onSave={handleSaveGroupFromBulk}
        preSelectedStoreIds={selectedStoreIds}
      />

      <DeleteConfirmationModal
        isOpen={Boolean(deleteConfirmation)}
        title={deleteConfirmationTitle}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default StoresListView;
